import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback
} from 'react'
import { useAnimate } from './use-animate'
import { useStatusMsg } from './use-status-msg'
import { useSocialState } from './use-social-state'
import { GRID_SIZE, XP_NEW_LEVEL, XP_NEW_DUNGEON, getInventoryLimit } from '../config/constants'
import {
  startQuest as apiStartQuest,
  completeQuestStep as apiCompleteStep,
  completeQuest as apiCompleteQuest,
} from 'actions/api'

const WorldContext = createContext({
  frame: {},
  spritesRef: { current: {} },
  addSprite: () => true
})

export const WorldProvider = ({ children, initialStats = {}, quests = [], dungeonSlug = null, saveId = null, onExit = null, collectTreasure = null, killMonster = null, onItemDrop = null, onItemPickup = null }) => {
  const [container, setContainer] = useState({ width: 300, height: 200 })
  const [dialog, setDialog] = useState([])
  const [shopInventory, setShopInventory] = useState([])
  const [showInventory, setShowInventory] = useState(false)
  const [frame, updateWorld] = useState()
  const [offset, setOffset] = useState({ x: 0, y: 0 })
  const pausedRef = useRef(false)

  // sprites lives in a ref — mutations never trigger re-renders.
  // Only playerPos is kept as state so Main.js can reactively pan the map.
  const spritesRef = useRef({})
  // Spatial index for solid sprites: "col,row" → Set of sprite ids. O(1) collision lookup.
  const solidGridRef = useRef({})
  // Spatial index for NPC sprites: "col,row" → Set of sprite ids. O(1) interaction lookup.
  const npcGridRef = useRef({})

  const [playerPos, setPlayerPos] = useState(null)
  const [target, setTarget] = useState()

  // ── Status message ──────────────────────────────────────────────────────────
  const { statusMsg, setStatusMsg } = useStatusMsg()

  // ── Social state (remote players + chat) ────────────────────────────────────
  const { remotePlayers, chatMessages, updateRemotePlayer, removeRemotePlayer, addChatMessage } = useSocialState(setStatusMsg)

  // ── Player stats ────────────────────────────────────────────────────────────
  const [health, setHealth] = useState(initialStats.health ?? 100)
  const [score, setScore] = useState(initialStats.score ?? 0)
  const [xp, setXp] = useState(initialStats.xp ?? 0)
  const [inventory, setInventory] = useState(() =>
    (initialStats.inventory ?? []).map((item, i) =>
      item.id?.startsWith('inv_') ? item : { ...item, id: `inv_${Date.now()}_${i}_${Math.random().toString(36).slice(2)}`, entityId: item.entityId ?? item.id }
    )
  )
  const inventoryRef = useRef(inventory)
  useEffect(() => { inventoryRef.current = inventory }, [inventory])
  const xpRef = useRef(xp)
  useEffect(() => { xpRef.current = xp }, [xp])
  const [wallet, setWallet] = useState(initialStats.wallet ?? 0)
  const [equippedWeaponId, setEquippedWeaponId] = useState(initialStats.equippedWeaponId ?? null)
  const equippedWeaponIdRef = useRef(initialStats.equippedWeaponId ?? null)
  useEffect(() => { equippedWeaponIdRef.current = equippedWeaponId }, [equippedWeaponId])
  const [equippedArmorId, setEquippedArmorId] = useState(initialStats.equippedArmorId ?? null)
  const equippedArmorIdRef = useRef(initialStats.equippedArmorId ?? null)
  useEffect(() => { equippedArmorIdRef.current = equippedArmorId }, [equippedArmorId])
  const [heldItemId, setHeldItemId] = useState(null)
  const [autoDefence, setAutoDefence] = useState(initialStats.autoDefence ?? false)
  const [gameOver, setGameOver] = useState(false)

  // ── Level / navigation state ─────────────────────────────────────────────────
  const [currentLevel, setCurrentLevel] = useState(0)
  const [visitedLevels, setVisitedLevels] = useState(initialStats.visitedLevels ?? [])
  const [visitedDungeons, setVisitedDungeons] = useState(initialStats.visitedDungeons ?? [])
  const [pendingSpawn, setPendingSpawn] = useState(null)
  const [pendingLadderChoice, setPendingLadderChoice] = useState(null)
  const pendingLadderChoiceRef = useRef(null)
  useEffect(() => { pendingLadderChoiceRef.current = pendingLadderChoice }, [pendingLadderChoice])
  const [droppedItems, setDroppedItems] = useState([])
  const [respawnKey, setRespawnKey] = useState(0)
  const respawnEntities = useCallback(() => {
    setDroppedItems([])
    setRespawnKey((k) => k + 1)
  }, [])

  const playerPosRef = useRef(null)
  useEffect(() => { playerPosRef.current = playerPos }, [playerPos])

  // ── Sprite map ───────────────────────────────────────────────────────────────
  // addSprite / removeSprite mutate refs directly — no re-render cascade.
  // Maintains solidGridRef and npcGridRef for O(1) spatial lookups.
  const addSprite = useCallback((position) => {
    const old = spritesRef.current[position.id]?.position
    if (old) {
      if (old.config?.solid !== false) {
        const k = `${Math.floor(old.x / GRID_SIZE)},${Math.floor(old.y / GRID_SIZE)}`
        solidGridRef.current[k]?.delete(position.id)
      }
      if (old.config?.npc) {
        const k = `${Math.floor(old.x / GRID_SIZE)},${Math.floor(old.y / GRID_SIZE)}`
        npcGridRef.current[k]?.delete(position.id)
      }
    }
    spritesRef.current[position.id] = { position }
    if (position.config?.solid !== false) {
      const k = `${Math.floor(position.x / GRID_SIZE)},${Math.floor(position.y / GRID_SIZE)}`
      if (!solidGridRef.current[k]) solidGridRef.current[k] = new Set()
      solidGridRef.current[k].add(position.id)
    }
    if (position.config?.npc) {
      const k = `${Math.floor(position.x / GRID_SIZE)},${Math.floor(position.y / GRID_SIZE)}`
      if (!npcGridRef.current[k]) npcGridRef.current[k] = new Set()
      npcGridRef.current[k].add(position.id)
    }
    if (position.id === 'player') setPlayerPos({ x: position.x, y: position.y })
  }, [])

  const removeSprite = useCallback(({ id }) => {
    const old = spritesRef.current[id]?.position
    if (old) {
      if (old.config?.solid !== false) {
        const k = `${Math.floor(old.x / GRID_SIZE)},${Math.floor(old.y / GRID_SIZE)}`
        solidGridRef.current[k]?.delete(id)
      }
      if (old.config?.npc) {
        const k = `${Math.floor(old.x / GRID_SIZE)},${Math.floor(old.y / GRID_SIZE)}`
        npcGridRef.current[k]?.delete(id)
      }
    }
    delete spritesRef.current[id]
    if (id === 'player') setPlayerPos(null)
  }, [])

  // ── Quest state ──────────────────────────────────────────────────────────────
  const [questProgress, setQuestProgress] = useState(initialStats.questProgress ?? {})
  const questProgressRef = useRef(questProgress)
  useEffect(() => { questProgressRef.current = questProgress }, [questProgress])

  const canPickUp = useCallback((type, name) => {
    const items = inventoryRef.current.filter((i) => i.type === type)
    const alreadyHave = items.some((i) => (i.name || i.type) === name)
    if (alreadyHave) return true
    return new Set(items.map((i) => i.name || i.type)).size < getInventoryLimit(xpRef.current)
  }, [])

  const gainXp = useCallback((amount) => setXp((p) => p + amount), [])

  const saveIdRef = useRef(saveId)
  useEffect(() => { saveIdRef.current = saveId }, [saveId])

  const startQuest = useCallback((questId) => {
    setQuestProgress(prev => {
      if (prev[questId]) return prev
      const quest = quests.find(q => q.id === questId)
      const preCompleted = []
      if (quest) {
        for (const step of (quest.steps || [])) {
          if (step.type === 'collect_item') {
            const have = inventoryRef.current.some(i => i.entityId === step.itemEntityId)
            if (have) preCompleted.push(step.id)
          }
        }
      }
      apiStartQuest(questId, saveIdRef.current).catch(() => {})
      for (const stepId of preCompleted) {
        apiCompleteStep(questId, stepId).catch(() => {})
      }
      return { ...prev, [questId]: { status: 'active', completedSteps: preCompleted } }
    })
  }, [quests])

  const completeQuestStep = useCallback((questId, stepId) => {
    setQuestProgress(prev => {
      const p = prev[questId]
      if (!p || p.status === 'completed' || p.completedSteps.includes(stepId)) return prev
      apiCompleteStep(questId, stepId).catch(() => {})
      return { ...prev, [questId]: { ...p, completedSteps: [...p.completedSteps, stepId] } }
    })
  }, [])

  const markLevelVisited = useCallback((lvl) => {
    setVisitedLevels((prev) => {
      if (prev.includes(lvl)) return prev
      gainXp(XP_NEW_LEVEL)
      return [...prev, lvl]
    })
  }, [gainXp])

  const markDungeonVisited = useCallback((key) => {
    setVisitedDungeons((prev) => {
      if (prev.includes(key)) return prev
      gainXp(XP_NEW_DUNGEON)
      return [...prev, key]
    })
  }, [gainXp])

  // ── Combat ───────────────────────────────────────────────────────────────────
  const equippedArmorRef = useRef(null)
  useEffect(() => {
    equippedArmorRef.current = equippedArmorId
      ? inventory.find((i) => i.id === equippedArmorId) ?? null
      : null
  }, [equippedArmorId, inventory])

  const takeDamage = useCallback((amount) => {
    const defense = equippedArmorRef.current?.value ?? 0
    const reduced = Math.max(1, amount - defense)
    setHealth((prev) => {
      const next = Math.max(0, prev - reduced)
      if (next === 0) setGameOver(true)
      return next
    })
  }, [])

  const healPlayer = useCallback((amount) => {
    setHealth((prev) => Math.min(initialStats.max_health ?? 100, prev + amount))
  }, [initialStats.max_health])

  const pendingDamageRef = useRef({})
  const dealDamage = useCallback((id, amount) => {
    pendingDamageRef.current[id] = (pendingDamageRef.current[id] || 0) + amount
  }, [])

  const attackFlashRef = useRef(null)

  // ── Inventory ────────────────────────────────────────────────────────────────
  const addToInventory = useCallback((item) => {
    const slotId = `inv_${Date.now()}_${Math.random().toString(36).slice(2)}`
    const expiresAt = item.lifespan > 0 ? Date.now() + item.lifespan * 1000 : null
    setInventory((prev) => [...prev, { ...item, id: slotId, entityId: item.entityId ?? item.id, expiresAt }])
    setScore((prev) => prev + (item.value || 10))
    if (item.type === 'weapon') setEquippedWeaponId((prev) => prev ?? slotId)
    if (item.type === 'armor') setEquippedArmorId((prev) => prev ?? slotId)
  }, [])

  const addToWallet = useCallback((amount) => {
    setWallet((prev) => prev + amount)
    setScore((prev) => prev + amount)
  }, [])

  const completeQuest = useCallback((questId) => {
    const quest = quests.find(q => q.id === questId)
    if (quest?.award?.xp) gainXp(quest.award.xp)
    if (quest?.award?.gold) addToWallet(quest.award.gold)
    apiCompleteQuest(questId).catch(() => {})
    setQuestProgress(prev => ({
      ...prev,
      [questId]: { ...prev[questId], status: 'completed' }
    }))
  }, [quests, gainXp, addToWallet])

  useEffect(() => {
    for (const [questId, progress] of Object.entries(questProgress)) {
      if (progress.status !== 'active') continue
      const quest = quests.find(q => q.id === questId)
      if (!quest) continue
      if (progress.completedSteps.length >= quest.steps.length) completeQuest(questId)
    }
  }, [questProgress, quests, completeQuest])

  const removeFromInventory = useCallback((id) => {
    setInventory((prev) => prev.filter((item) => item.id !== id))
    setEquippedWeaponId((prev) => (prev === id ? null : prev))
    setEquippedArmorId((prev) => (prev === id ? null : prev))
    setHeldItemId((prev) => (prev === id ? null : prev))
  }, [])

  const holdItem = useCallback((id) => {
    setHeldItemId((prev) => (prev === id ? null : id))
  }, [])

  const equipWeapon = useCallback((id) => {
    const prev = equippedWeaponIdRef.current
    if (prev && prev !== id) {
      const old = inventoryRef.current.find((i) => i.id === prev)
      if (old) setStatusMsg(`${old.name || 'weapon'} unequipped`)
    }
    setEquippedWeaponId((p) => (p === id ? null : id))
  }, [setStatusMsg])

  const equipArmor = useCallback((id) => {
    const prev = equippedArmorIdRef.current
    if (prev && prev !== id) {
      const old = inventoryRef.current.find((i) => i.id === prev)
      if (old) setStatusMsg(`${old.name || 'armor'} unequipped`)
    }
    setEquippedArmorId((p) => (p === id ? null : id))
  }, [setStatusMsg])

  const toggleAutoDefence = useCallback(() => {
    setAutoDefence((prev) => !prev)
  }, [])

  // ── Item expiry ───────────────────────────────────────────────────────────────
  useEffect(() => {
    const id = setInterval(() => {
      const now = Date.now()
      const expiredInv = inventoryRef.current.filter((i) => i.expiresAt && i.expiresAt <= now)
      if (expiredInv.length) {
        const names = expiredInv.map((i) => i.name || i.type).join(', ')
        setStatusMsg(`${names} burned out`)
        setInventory((prev) => prev.filter((i) => !i.expiresAt || i.expiresAt > now))
      }
      setDroppedItems((prev) => {
        const expired = prev.filter((i) => i.expiresAt && i.expiresAt <= now)
        if (!expired.length) return prev
        expired.forEach((i) => { delete spritesRef.current[i.id] })
        return prev.filter((i) => !i.expiresAt || i.expiresAt > now)
      })
    }, 1000)
    return () => clearInterval(id)
  }, [setStatusMsg])

  // ── Drops ────────────────────────────────────────────────────────────────────
  const dropItem = useCallback((item) => {
    const pos = playerPosRef.current
    if (!pos) return
    const baseX = Math.floor(pos.x / GRID_SIZE) * GRID_SIZE
    const baseY = Math.floor(pos.y / GRID_SIZE) * GRID_SIZE
    const isSolidAt = (tx, ty) =>
      Object.values(spritesRef.current).some(
        (s) => s.position?.config?.solid && s.position.x === tx && s.position.y === ty
      )
    const offsets = [[0, 1], [1, 0], [0, -1], [-1, 0]]
    const [dx, dy] = offsets.find(([dc, dr]) => !isSolidAt(baseX + dc * GRID_SIZE, baseY + dr * GRID_SIZE)) ?? [0, 0]
    const dropped = { ...item, id: `drop_${Date.now()}_${item.id}`, x: baseX + dx * GRID_SIZE, y: baseY + dy * GRID_SIZE, dropped: true }
    setInventory((prev) => prev.filter((i) => i.id !== item.id))
    setDroppedItems((prev) => [...prev, dropped])
    onItemDrop?.(dropped)
  }, [onItemDrop])

  const addDroppedItem = useCallback((item) => {
    setDroppedItems((prev) => [...prev, item])
  }, [])

  const pickupDroppedItem = useCallback((id) => {
    setDroppedItems((prev) => prev.filter((i) => i.id !== id))
    onItemPickup?.(id)
  }, [onItemPickup])

  // ── Shop ─────────────────────────────────────────────────────────────────────
  const openShop = useCallback((items) => {
    if (items?.length) setShopInventory([...items])
  }, [])

  const closeShop = useCallback(() => setShopInventory([]), [])

  const scoreRef = useRef(score)
  useEffect(() => { scoreRef.current = score }, [score])
  const walletRef = useRef(wallet)
  useEffect(() => { walletRef.current = wallet }, [wallet])
  const boughtCounterRef = useRef(0)

  const buyItem = useCallback((item) => {
    const price = item.price || 0
    if (walletRef.current < price) return
    setWallet((prev) => prev - price)
    setInventory((inv) => [...inv, { id: `bought_${++boughtCounterRef.current}`, type: item.type, name: item.name, value: item.value }])
    setShopInventory((shop) => shop.filter((i) => i.id !== item.id))
  }, [])

  // ── Animation loop ───────────────────────────────────────────────────────────
  const paused = dialog.length > 0 || showInventory
  useEffect(() => { pausedRef.current = paused }, [paused])

  const animateCallback = useCallback((next) => {
    if (!pausedRef.current) updateWorld(next)
  }, [])
  useAnimate(animateCallback)

  return (
    <WorldContext.Provider
      value={{
        addSprite,
        solidGridRef,
        npcGridRef,
        canPickUp,
        addToInventory,
        addToWallet,
        removeFromInventory,
        equipWeapon,
        equippedWeaponId,
        equipArmor,
        equippedArmorId,
        holdItem,
        heldItemId,
        autoDefence,
        toggleAutoDefence,
        wallet,
        container,
        currentLevel,
        dialog,
        paused,
        showInventory,
        setShowInventory,
        shopInventory,
        openShop,
        closeShop,
        buyItem,
        frame,
        gameOver,
        health,
        inventory,
        maxHealth: initialStats.max_health ?? 100,
        offset,
        pendingLadderChoice,
        pendingLadderChoiceRef,
        pendingSpawn,
        playerPos,
        removeSprite,
        remotePlayers,
        removeRemotePlayer,
        chatMessages,
        addChatMessage,
        setPendingLadderChoice,
        score,
        xp,
        gainXp,
        visitedLevels,
        markLevelVisited,
        visitedDungeons,
        markDungeonVisited,
        setContainer,
        setCurrentLevel,
        setDialog,
        setOffset,
        setPendingSpawn,
        setTarget,
        spritesRef,
        takeDamage,
        healPlayer,
        dealDamage,
        pendingDamageRef,
        attackFlashRef,
        target,
        updateRemotePlayer,
        onExit,
        collectTreasure,
        killMonster,
        droppedItems,
        dropItem,
        addDroppedItem,
        pickupDroppedItem,
        respawnKey,
        respawnEntities,
        quests,
        questProgress,
        questProgressRef,
        dungeonSlug,
        startQuest,
        completeQuestStep,
        completeQuest,
        statusMsg,
        setStatusMsg,
      }}
    >
      {children}
    </WorldContext.Provider>
  )
}

export const useWorld = () => useContext(WorldContext)
